// src/services/crypto/conversationEncryptionService.ts

import {
  backfillDeviceKeys,
  fetchConversationEncryptionState,
  markPassphraseSet,
  registerCurrentPublicKey,
  resetConversationEncryptionIdentity,
} from "./deviceKeyApi";

import {
  commitStagedKeyPair,
  discardStagedKeyPair,
  getPublicKeyPem,
  hasPrivateKeyStored,
  invalidatePrivateKeyPresenceCache,
  setPublicKeyPem,
  stageKeyPair,
  storeEncryptedPrivatePem,
} from "../../utils/crypto/keyStorageSecure";

import {
  backupPrivateKey,
  restorePrivateKeyLatest,
} from "../../utils/crypto/keyBackup";

import {
  pkcs8ToPrivatePem,
  spkiToPublicPem,
} from "../../utils/crypto/pem";

import {
  computeKeyIdFromPublicPem,
} from "../../utils/crypto/keyUtils";

import {
  clearDeviceId,
} from "../../utils/common/deviceHelpers";

import {
  getDeviceIdAsync,
} from "../../utils/keyManager";

import {
  performPopWithPayload,
  PopResult,
} from "../../utils/crypto/pop";

import {
  ConversationEncryptionState,
  LocalConversationKeyState,
} from "../../types/crypto/cryptoTypes";

export type ConversationIdentityPreparationResult =
  | "ready"
  | "backup_required"
  | "restore_required"
  | "reset_required"
  | "recovery_metadata_incomplete"
  | "unavailable";

export type EncryptionIdentityResetResult = {
  serverAccepted: boolean;
  verified: boolean;
  verificationResult: PopResult;
  deviceId: string;
};

export async function getLocalConversationKeyState(): Promise<LocalConversationKeyState> {
  const [
    publicKey,
    privateKeyExists,
  ] = await Promise.all([
    getPublicKeyPem(),
    hasPrivateKeyStored(),
  ]);

  if (publicKey && privateKeyExists) {
    return "complete";
  }

  if (!publicKey && !privateKeyExists) {
    return "missing";
  }

  return "incomplete";
}

export async function generateAndStoreInitialConversationKeyPair(): Promise<void> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([
        1,
        0,
        1,
      ]),
      hash: "SHA-256",
    },
    true,
    [
      "encrypt",
      "decrypt",
    ],
  );

  const [
    publicBuffer,
    privateBuffer,
  ] = await Promise.all([
    crypto.subtle.exportKey(
      "spki",
      keyPair.publicKey,
    ),
    crypto.subtle.exportKey(
      "pkcs8",
      keyPair.privateKey,
    ),
  ]);

  try {
    await Promise.all([
      setPublicKeyPem(
        spkiToPublicPem(publicBuffer),
      ),
      storeEncryptedPrivatePem(
        pkcs8ToPrivatePem(privateBuffer),
      ),
    ]);
  } finally {
    new Uint8Array(privateBuffer).fill(0);
  }

  invalidatePrivateKeyPresenceCache();
}

export async function silentlyPrepareConversationIdentity(): Promise<ConversationIdentityPreparationResult> {
  const remoteState =
    await fetchConversationEncryptionState();

  let localState =
    await getLocalConversationKeyState();

  if (localState === "incomplete") {
    return "unavailable";
  }

  if (localState === "complete") {
    await registerAndVerifyCurrentKey();
    return remoteState.has_backup
      ? "ready"
      : "backup_required";
  }

  switch (remoteState.state) {
    case "never_initialized":
      if (!remoteState.can_silently_provision) {
        return "unavailable";
      }

      await generateAndStoreInitialConversationKeyPair();
      localState =
        await getLocalConversationKeyState();

      if (localState !== "complete") {
        return "unavailable";
      }

      await registerAndVerifyCurrentKey();

      return "backup_required";

    case "backup_available":
      return "restore_required";

    case "initialized_without_backup":
      return "reset_required";

    case "recovery_metadata_only":
      return "recovery_metadata_incomplete";
  }
}

export async function registerAndVerifyCurrentKey(): Promise<{
  verified: boolean;
  verificationResult: PopResult;
}> {
  const response =
    await registerCurrentPublicKey(
      false,
      false,
    );

  if ("skipped" in response) {
    return {
      verified: false,
      verificationResult: "unexpected",
    };
  }

  const verificationResult =
    await performPopWithPayload(
      response.pop,
    );

  return {
    verified:
      verificationResult === "ok" ||
      verificationResult === "already",
    verificationResult,
  };
}

export async function restoreConversationIdentity(
  passphrase: string,
): Promise<void> {
  await restorePrivateKeyLatest(
    passphrase,
  );

  invalidatePrivateKeyPresenceCache();
  await clearDeviceId(true);

  await getDeviceIdAsync();

  const registration =
    await registerAndVerifyCurrentKey();

  if (!registration.verified) {
    throw new Error(
      "The encryption key was restored, but this browser could not verify possession of it yet.",
    );
  }

  await backfillDeviceKeys();
}

export async function createConversationRecoveryBackup(
  passphrase: string,
): Promise<void> {
  await registerAndVerifyCurrentKey();
  await backupPrivateKey(passphrase);
  await markPassphraseSet(
    "PBKDF2",
    600_000,
  );
}

export async function resetConversationIdentity(
  accountPassword: string,
  confirmation: string,
): Promise<EncryptionIdentityResetResult> {
  const normalizedConfirmation =
    confirmation.trim().toUpperCase();

  if (!accountPassword) {
    throw new Error(
      "Your TownLIT account password is required.",
    );
  }

  if (normalizedConfirmation !== "RESET") {
    throw new Error(
      "Enter RESET to confirm the encryption identity reset.",
    );
  }

  const keyPair = await crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([
        1,
        0,
        1,
      ]),
      hash: "SHA-256",
    },
    true,
    [
      "encrypt",
      "decrypt",
    ],
  );

  const [
    publicBuffer,
    privateBuffer,
  ] = await Promise.all([
    crypto.subtle.exportKey(
      "spki",
      keyPair.publicKey,
    ),
    crypto.subtle.exportKey(
      "pkcs8",
      keyPair.privateKey,
    ),
  ]);

  const publicPem =
    spkiToPublicPem(publicBuffer);

  const privatePem =
    pkcs8ToPrivatePem(privateBuffer);

  const newDeviceId =
    await computeKeyIdFromPublicPem(publicPem);

  let serverAccepted = false;

  try {
    await stageKeyPair(
      publicPem,
      privatePem,
    );

    const response =
      await resetConversationEncryptionIdentity(
        accountPassword,
        normalizedConfirmation,
        publicPem,
        newDeviceId,
      );

    serverAccepted = true;

    // The server has committed the new identity.
    // The new key must now remain active even if PoP fails.
    await commitStagedKeyPair();

    invalidatePrivateKeyPresenceCache();
    await clearDeviceId(true);

    const canonicalDeviceId =
      await getDeviceIdAsync();

    if (
      canonicalDeviceId.toLowerCase() !==
      newDeviceId.toLowerCase()
    ) {
      throw new Error(
        "The browser generated an unexpected encryption device identifier.",
      );
    }

    const verificationResult =
      await performPopWithPayload(
        response.pop,
      );

    const verified =
      verificationResult === "ok" ||
      verificationResult === "already";

    return {
      serverAccepted: true,
      verified,
      verificationResult,
      deviceId: canonicalDeviceId,
    };
  } catch (error) {
    if (!serverAccepted) {
      // Old active key remains untouched.
      await discardStagedKeyPair();
    }

    throw error;
  } finally {
    new Uint8Array(privateBuffer).fill(0);
  }
}

export async function getConversationEncryptionSnapshot(): Promise<{
  localState: LocalConversationKeyState;
  remoteState: ConversationEncryptionState;
}> {
  const [
    localState,
    remoteState,
  ] = await Promise.all([
    getLocalConversationKeyState(),
    fetchConversationEncryptionState(),
  ]);

  return {
    localState,
    remoteState,
  };
}